import React, {useState, useEffect} from 'react';
import {GPXTrace, gpxTraceService} from '../../services/gpxTraceService';
import './EditTraceModal.css';

interface EditTraceModalProps {
    trace: GPXTrace | null;
    onClose: () => void;
    onSuccess: (updated: GPXTrace) => void;
}

const EditTraceModal: React.FC<EditTraceModalProps> = ({trace, onClose, onSuccess}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (trace) {
            setName(trace.original_name);
            setDescription(trace.description ?? '');
            setError(null);
        }
    }, [trace]);

    if (!trace) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Le nom ne peut pas être vide.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const updated = await gpxTraceService.updateTrace(trace.path, {
                original_name: trimmedName,
                description: description.trim() || undefined,
            });
            onSuccess(updated);
        } catch {
            setError('Erreur lors de la mise à jour de la trace.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="edit-trace-overlay" onClick={onClose}>
            <div className="edit-trace-modal" onClick={e => e.stopPropagation()}>
                <div className="edit-trace-header">
                    <h3>Éditer la trace</h3>
                    <button className="edit-trace-close" onClick={onClose} aria-label="Fermer">✕</button>
                </div>
                <form className="edit-trace-form" onSubmit={handleSubmit}>
                    <div className="edit-trace-field">
                        <label htmlFor="trace-name">Nom</label>
                        <input
                            id="trace-name"
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            maxLength={200}
                            autoFocus
                        />
                    </div>
                    <div className="edit-trace-field">
                        <label htmlFor="trace-desc">Description</label>
                        <textarea
                            id="trace-desc"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                            maxLength={1000}
                        />
                    </div>
                    {error && <div className="edit-trace-error">{error}</div>}
                    <div className="edit-trace-actions">
                        <button type="button" className="edit-trace-btn secondary" onClick={onClose} disabled={isLoading}>
                            Annuler
                        </button>
                        <button type="submit" className="edit-trace-btn primary" disabled={isLoading}>
                            {isLoading ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditTraceModal;
